import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, Star, MapPin, Award, Phone, Mail, ArrowLeft, MessageSquarePlus } from "lucide-react";
import { api } from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import { profileAvatar } from "../lib/avatar";
import WriteReviewSheet from "../components/WriteReviewSheet";
import { useAuth } from "../context/AuthContext";

export default function PartnerProfile() {
    const { id } = useParams();
    const { user } = useAuth();
    const [p, setP] = useState(null);
    const [props, setProps] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [reviewOpen, setReviewOpen] = useState(false);

    const loadReviews = () => api.get("/reviews", { params: { target_id: id } })
        .then((r) => setReviews(r.data || []));

    useEffect(() => {
        api.get(`/partners/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
        api.get("/properties", { params: { limit: 100 } }).then((r) => {
            setProps((r.data.items || []).filter((x) => x.owner_id === id));
        });
        loadReviews();
    }, [id]);

    if (!p) return <div className="min-h-screen flex items-center justify-center text-gold-500">Loading…</div>;

    const isSelf = user?.id === id;

    return (
        <div className="min-h-screen bg-navy-900">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
                <Link to="/brokers" className="text-sm text-white/60 hover:text-gold-500 inline-flex items-center gap-2 mb-6">
                    <ArrowLeft className="w-4 h-4" /> Back
                </Link>
                <div className="rounded-3xl bg-navy-800 border border-gold-500/20 p-8 lg:p-10 grid grid-cols-1 md:grid-cols-12 gap-8">
                    <div className="md:col-span-3 flex flex-col items-center text-center">
                        <div className="relative">
                            <img src={profileAvatar(p, 256)} alt={p.name} className="w-32 h-32 rounded-full object-cover ring-4 ring-gold-500/30" />
                            {p.verified && <div className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-gold-500 flex items-center justify-center ring-4 ring-navy-800"><ShieldCheck className="w-4 h-4 text-navy-900" strokeWidth={3} /></div>}
                        </div>
                        <div className="mt-4 flex items-center gap-1 text-gold-500"><Star className="w-4 h-4 fill-gold-500" /> {p.rating?.toFixed(1) || "—"}</div>
                        <div className="text-xs text-white/50">{p.reviews_count || 0} reviews</div>
                    </div>
                    <div className="md:col-span-9">
                        <div className="label-overline mb-2">{p.role.replace("_", " ")}</div>
                        <h1 className="font-heading text-4xl font-bold text-white">{p.name}</h1>
                        <div className="text-white/65 flex items-center gap-2 mt-2"><MapPin className="w-4 h-4 text-gold-500" /> {p.city}</div>
                        {p.bio && <p className="text-white/75 leading-relaxed mt-4 max-w-2xl">{p.bio}</p>}
                        <div className="flex flex-wrap gap-2 mt-5">
                            {p.rera_number && <Badge label={`RERA · ${p.rera_number}`} />}
                            {p.bar_council_id && <Badge label={`Bar · ${p.bar_council_id}`} />}
                            {p.experience_years && <Badge label={`${p.experience_years}+ Years Experience`} />}
                        </div>
                        <div className="flex gap-3 mt-6 flex-wrap">
                            {p.phone && <a href={`tel:${p.phone}`} className="px-5 py-2.5 rounded-md bg-gold-500 text-navy-900 font-semibold text-sm hover:bg-gold-100 inline-flex items-center gap-2"><Phone className="w-4 h-4" /> Call</a>}
                            {p.email && <a href={`mailto:${p.email}`} className="px-5 py-2.5 rounded-md border border-gold-500/40 text-gold-500 font-medium text-sm hover:bg-gold-500/10 inline-flex items-center gap-2"><Mail className="w-4 h-4" /> Email</a>}
                            {!isSelf && (
                                <button
                                    type="button"
                                    onClick={() => setReviewOpen(true)}
                                    data-testid="write-review-btn"
                                    className="px-5 py-2.5 rounded-md border border-gold-500/40 text-gold-500 font-medium text-sm hover:bg-gold-500/10 inline-flex items-center gap-2"
                                >
                                    <MessageSquarePlus className="w-4 h-4" /> Write a Review
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {props.length > 0 && (
                    <div className="mt-12">
                        <h2 className="font-heading text-2xl font-bold text-white mb-6">Active listings ({props.length})</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
                            {props.map((x) => <PropertyCard key={x.id} property={x} />)}
                        </div>
                    </div>
                )}

                {/* Customer reviews section — always shown so users can leave the first one */}
                <div className="mt-12">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                        <h2 className="font-heading text-2xl font-bold text-white">
                            Customer reviews
                            {reviews.length > 0 && <span className="ml-2 text-sm text-white/50 font-normal">({reviews.length})</span>}
                        </h2>
                        {!isSelf && (
                            <button
                                type="button"
                                onClick={() => setReviewOpen(true)}
                                data-testid="write-review-btn-reviews"
                                className="px-4 py-2 rounded-md bg-gold-500 text-navy-900 font-semibold text-sm hover:bg-gold-100 inline-flex items-center gap-2"
                            >
                                <MessageSquarePlus className="w-4 h-4" /> Write a Review
                            </button>
                        )}
                    </div>
                    {reviews.length === 0 ? (
                        <div className="rounded-xl bg-navy-800 border border-white/5 p-6 text-center text-white/60 text-sm" data-testid="partner-reviews-empty">
                            No reviews yet.{!isSelf && " Be the first to share your experience."}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {reviews.map((r) => (
                                <div key={r.id} className="rounded-xl bg-navy-800 border border-white/5 p-5">
                                    <div className="flex items-center justify-between">
                                        <div className="font-semibold text-white">{r.user_name}</div>
                                        <div className="flex items-center gap-1 text-gold-500"><Star className="w-4 h-4 fill-gold-500" /> {r.rating}</div>
                                    </div>
                                    {r.comment && <p className="text-white/75 mt-2 text-sm">{r.comment}</p>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <WriteReviewSheet
                open={reviewOpen}
                onClose={() => setReviewOpen(false)}
                targetId={id}
                targetType={p.role}
                targetName={p.name}
                onSubmitted={() => loadReviews()}
            />
        </div>
    );
}

function Badge({ label }) {
    return <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold-500/10 border border-gold-500/30 text-xs font-medium text-gold-500"><Award className="w-3 h-3" /> {label}</span>;
}
